/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 * Supports both Azure AD/MSAL and database authentication
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import {
  msalInstance,
  initializeMsal,
  login,
  logout,
  getAccessToken,
  getActiveAccount,
  trySilentSso,
  accountToUser,
} from '@/services/authService';
import { dbAuthService } from '@/services/dbAuthService';
import {
  checkUserMappedGroups,
  getUserDepartment,
  mapGroupsToRole,
} from '@/services/graphService';
import {
  getPublicConfig,
  PUBLIC_CONFIG_FALLBACK,
  type PublicConfig,
} from '@/services/publicConfigService';
import type { AuthContextType, User, AuthMethod } from '@/types';
import { DEV_CONFIG } from '@/config/devConfig';
import { reportError } from '@/services/errorTelemetry';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState<AuthMethod | null>(null);
  // Populated by GET /api/public-config on mount; null while the fetch is
  // in flight, then either the server response or PUBLIC_CONFIG_FALLBACK on
  // failure. attemptEntraLogin and handleLoginWithDatabase read this state
  // directly — by the time the user clicks a Sign-In button, the fetch has
  // resolved.
  const [publicConfig, setPublicConfig] = useState<PublicConfig | null>(null);

  const isEntraAuthEnabled =
    publicConfig?.features.enableEntraAuth
    ?? PUBLIC_CONFIG_FALLBACK.features.enableEntraAuth;
  const isDatabaseAuthEnabled =
    publicConfig?.features.enableDatabaseAuth
    ?? PUBLIC_CONFIG_FALLBACK.features.enableDatabaseAuth;

  // Initialize authentication on mount
  useEffect(() => {
    async function initialize() {
      try {
        // Fetch public config first so we know which auth methods are
        // enabled and have the Entra group→role mappings ready before MSAL
        // returns. Falls back to build-time defaults if the backend is
        // unreachable (e.g. dev with the API server down).
        const config = await getPublicConfig();
        setPublicConfig(config);
        const localIsEntraEnabled = config.features.enableEntraAuth;
        const localIsDbEnabled = config.features.enableDatabaseAuth;
        const groupMappings = config.entraGroupMappings;
        // Mock authentication in development mode
        if (DEV_CONFIG.enableMockAuth) {
          console.log('🔓 Mock authentication enabled (dev mode)');
          setUser(DEV_CONFIG.mockUser);
          setIsAuthenticated(true);
          setAuthMethod('mock');
          setIsLoading(false);
          return;
        }

        // Check for existing database auth session
        if (localIsDbEnabled && dbAuthService.hasToken()) {
          const dbUser = await dbAuthService.getCurrentUser();
          if (dbUser) {
            const userData = {
              id: dbUser.id.toString(),
              name: dbUser.username,
              email: dbUser.email,
              role: dbUser.role,
              forcePasswordChange: dbUser.force_password_change,
            };
            setUser(userData);
            setIsAuthenticated(true);
            setAuthMethod('database');


            setIsLoading(false);
            return;
          }
        }

        // Check for existing Entra ID session
        if (localIsEntraEnabled) {
          await initializeMsal();

          // First check MSAL localStorage cache, then fall back to silent SSO
          // using the browser's existing Entra session cookie so users are never
          // prompted to log in again as long as their Entra session is active.
          const account = getActiveAccount() ?? await trySilentSso();

          if (account) {
            let userData = accountToUser(account);

            // Apply Entra group-to-role mapping (groupMappings sourced from
            // the public-config fetch at the top of initialize())
            if (groupMappings.length > 0) {
              try {
                // Ask Graph "of these mapped group IDs, which is the user
                // a member of?" via /me/checkMemberGroups. Returns just
                // the matching subset — works regardless of how many
                // total groups the user belongs to (we have customers in
                // 4000+ groups, where the old "fetch all groups" approach
                // either truncated at the page size or bloated downstream
                // headers past server limits).
                const mappedIds = await checkUserMappedGroups(
                  groupMappings.map((m) => m.groupId),
                );
                console.log('Mapped groups (existing session):', mappedIds);

                const mappedRole = mapGroupsToRole(mappedIds, groupMappings);
                console.log('Mapped role (existing session):', mappedRole);

                // Security check: if no mapped group matched, the user is
                // not authorized. (mappedIds is already filtered to only
                // mapped groups, so its emptiness is the answer.)
                if (mappedIds.length === 0) {
                  // Silent SSO may have picked up the wrong account
                  // (common in Edge, which surfaces the browser's
                  // signed-in Microsoft account). Clear MSAL state
                  // silently and fall through to the login screen.
                  console.warn(
                    'Silent SSO account not in any configured Entra groups — clearing session',
                  );
                  await logout();
                  return;
                }

                // Update user data with mapped role
                userData = { ...userData, role: mappedRole };
              } catch (groupErr) {
                // Graph API call failed (e.g. token for wrong tenant, network block).
                // Clear silently so the user can sign in interactively.
                console.warn('Could not verify group membership during silent SSO — clearing session:', groupErr);
                await logout();
                return;
              }
            }

            // Fetch department from Graph and cache it. Forwarded as
            // `X-User-Department` on every authenticated backend call
            // so the backend can sync `users.department`. Failure to
            // fetch (e.g. service-account user, Graph rate-limited)
            // never blocks sign-in.
            try {
              const dept = await getUserDepartment();
              if (dept) localStorage.setItem('user_department', dept);
              else localStorage.removeItem('user_department');
            } catch (deptErr) {
              console.warn('Could not fetch user department from Graph:', deptErr);
            }
            // No group-mappings fallback. The pre-PR-5 code granted admin
            // to the "first Entra user" (tracked via stellar_admin_users
            // in localStorage) when no mappings were configured. With
            // mappings now persisted in admin_config and seeded by the
            // backend default, that fallback was dead code on every real
            // deployment and only confused fresh dev installs.

            setUser(userData);
            setIsAuthenticated(true);
            setAuthMethod('entra');
          }
        }
      } catch (err) {
        reportError(err, { area: 'auth', step: 'init' }, 'error');
        console.error('Auth initialization error:', err);
        setError('Failed to initialize authentication');
      } finally {
        setIsLoading(false);
      }
    }

    initialize();
    // Empty dependency array - only run on mount
    // Config values are read from localStorage
    // and don't need to trigger re-init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Entra ID login.
  //
  // First attempt is silent (no account picker) so single-account users sign
  // in with one click. If the silent attempt lands on an account that fails
  // the Entra group check — common when the browser's ambient Microsoft
  // session is a different account than the one the user wanted — we DON'T
  // show an "Access denied" banner. Instead we transparently retry with
  // `prompt: 'select_account'` so the user can pick the right account.
  // Only if that retry also fails do we conclude the user really does not
  // have access and show the banner.
  // Handler intentionally not useCallback-wrapped per the perf-phase1
  // plan; identity changes still propagate via the contextValue useMemo
  // deps array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLogin = async () => {
    await attemptEntraLogin({ forceAccountPicker: false });
  };

  const attemptEntraLogin = async (opts: { forceAccountPicker: boolean }) => {
    if (!isEntraAuthEnabled) {
      setError('Entra ID authentication is not enabled');
      throw new Error('Entra ID authentication is not enabled');
    }

    setError(null);
    setIsLoading(true);

    // When we deliberately surface a specific error (access denied,
    // group-membership check failed), we set this flag so the catch
    // block below doesn't overwrite it with a generic "Failed to log in".
    let explicitErrorSet = false;

    try {
      // Mock login in development mode
      if (DEV_CONFIG.enableMockAuth) {
        console.log('🔓 Mock login (dev mode)');
        setUser(DEV_CONFIG.mockUser);
        setIsAuthenticated(true);
        setAuthMethod('mock');
        setIsLoading(false);
        return;
      }

      const result = await login(
        opts.forceAccountPicker ? { forceAccountPicker: true } : undefined
      );
      if (result.account) {
        let userData = accountToUser(result.account);

        // Apply Entra group-to-role mapping. Sourced from the public-config
        // fetch at AuthProvider mount; defaults to [] if the fetch failed.
        const groupMappings = publicConfig?.entraGroupMappings ?? [];
        if (groupMappings.length > 0) {
          // Ask Graph "is this user a member of any of these mapped
          // group IDs?" via /me/checkMemberGroups. Returns just the
          // matching subset — works for users in 4000+ total groups
          // because the intersection happens server-side.
          let mappedIds: string[];
          let mappedRole: ReturnType<typeof mapGroupsToRole>;
          try {
            mappedIds = await checkUserMappedGroups(
              groupMappings.map((m) => m.groupId),
            );
            console.log('Mapped groups:', mappedIds);
            mappedRole = mapGroupsToRole(mappedIds, groupMappings);
            console.log('Mapped role:', mappedRole);
          } catch (groupErr) {
            reportError(groupErr, { area: 'auth', step: 'entra-groups' }, 'error');
            console.error('Failed to fetch user groups or map role:', groupErr);
            setError('Unable to verify group membership. Please try again or contact your administrator.');
            explicitErrorSet = true;
            setIsAuthenticated(false);
            await logout();
            throw groupErr;
          }

          // Security check: if no mapped group matched, the user is not
          // authorized.
          if (mappedIds.length === 0) {
            // Silent first attempt hit an unauthorized account — retry
            // with the account picker so the user can pick a different
            // one, without ever showing the "Access denied" banner.
            if (!opts.forceAccountPicker) {
              console.warn('Silent login picked an unauthorized account — retrying with account picker');
              setIsLoading(false);
              await attemptEntraLogin({ forceAccountPicker: true });
              return;
            }

            // Picker retry also failed — the user deliberately picked an
            // unauthorized account. Banner is appropriate here.
            console.warn('Access denied: User is not in any configured Entra groups');
            setError('Access denied: You are not authorized to access this application. Please contact your administrator.');
            explicitErrorSet = true;
            setIsAuthenticated(false);
            setIsLoading(false);
            await logout();
            throw new Error('Access denied: User not in any configured groups');
          }

          // Update user data with mapped role
          userData = { ...userData, role: mappedRole };
        }

        // Fetch department from Graph (see comment in initialize() for
        // rationale). Errors are best-effort; sign-in is never blocked.
        try {
          const dept = await getUserDepartment();
          if (dept) localStorage.setItem('user_department', dept);
          else localStorage.removeItem('user_department');
        } catch (deptErr) {
          console.warn('Could not fetch user department from Graph:', deptErr);
        }
        // (PR 5) Removed the first-Entra-user-becomes-admin fallback that
        // tracked itself via stellar_admin_users in localStorage. Roles
        // now come from admin_config.authDatabase.entraGroupMappings,
        // seeded by the backend default and editable via the admin UI.

        setUser(userData);
        setIsAuthenticated(true);
        setAuthMethod('entra');
      }
    } catch (err) {
      console.error('Login error:', err);
      // Only set the generic failure banner if we haven't already surfaced a
      // specific one (e.g. "Access denied", "Unable to verify group
      // membership"). Explicit errors set in the try block stay; explicit
      // errors set inside a recursive inner call are detected via the error
      // message since the inner's local `explicitErrorSet` flag isn't
      // visible to this outer catch.
      const message = err instanceof Error ? err.message : '';
      const isHandled =
        explicitErrorSet ||
        /Access denied/i.test(message) ||
        /group membership/i.test(message);
      if (!isHandled) {
        reportError(err, { area: 'auth', step: 'login' }, 'error');
        setError('Failed to log in. Please try again.');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle database login
  // eslint-disable-next-line react-hooks/exhaustive-deps -- see handleLogin
  const handleLoginWithDatabase = async (
    username: string,
    password: string
  ) => {
    if (!isDatabaseAuthEnabled) {
      setError('Database authentication is not enabled');
      throw new Error('Database authentication is not enabled');
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await dbAuthService.login({ username, password });

      const userData = {
        id: response.user.id.toString(),
        name: response.user.username,
        email: response.user.email,
        role: response.user.role,
        forcePasswordChange: response.user.force_password_change,
      };

      setUser(userData);
      setIsAuthenticated(true);
      setAuthMethod('database');
    } catch (err) {
      reportError(err, { area: 'auth', step: 'db-login' }, 'error');
      console.error('Database login error:', err);
      setError('Invalid username or password');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle logout
  // eslint-disable-next-line react-hooks/exhaustive-deps -- see handleLogin
  const handleLogout = async () => {
    setError(null);
    setIsLoading(true);

    try {
      // Logout based on current auth method
      if (authMethod === 'database') {
        await dbAuthService.logout();
      } else if (authMethod === 'entra') {
        await logout();
      }

      // Clear cached Graph-sourced department so the next sign-in's
      // value can't leak into a different user's session.
      localStorage.removeItem('user_department');

      setUser(null);
      setIsAuthenticated(false);
      setAuthMethod(null);
    } catch (err) {
      reportError(err, { area: 'auth', step: 'logout' }, 'error');
      console.error('Logout error:', err);
      setError('Failed to log out. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Get access token (supports both auth methods)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- see handleLogin
  const handleGetAccessToken = async (): Promise<string> => {
    if (DEV_CONFIG.enableMockAuth) {
      return DEV_CONFIG.mockAccessToken;
    }

    if (authMethod === 'database') {
      const token = localStorage.getItem('db_auth_token');
      if (!token) {
        throw new Error('No database auth token found');
      }
      return token;
    }

    if (authMethod === 'entra') {
      return getAccessToken();
    }

    throw new Error('No authentication method active');
  };

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      error,
      authMethod,
      login: handleLogin,
      loginWithDatabase: handleLoginWithDatabase,
      logout: handleLogout,
      getAccessToken: handleGetAccessToken,
      isEntraAuthEnabled,
      isDatabaseAuthEnabled,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      error,
      authMethod,
      handleLogin,
      handleLoginWithDatabase,
      handleLogout,
      handleGetAccessToken,
      isEntraAuthEnabled,
      isDatabaseAuthEnabled,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export MSAL instance for MsalProvider
export { msalInstance };
